import type { DragonDossier, SavedDossierResponse } from "../types";
import { apiPath, stripAppBase } from "./urls";

const JSON_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json"
};

function editTokenFromLocation(): string | null {
  const match = window.location.hash.match(/(?:^#|&)edit=([^&]+)/);
  if (match) {
    const token = decodeURIComponent(match[1]);
    const id = dossierIdFromLocation();
    if (id) localStorage.setItem(`tiamat-edit-${id}`, token);
    return token;
  }
  const id = dossierIdFromLocation();
  return id ? localStorage.getItem(`tiamat-edit-${id}`) : null;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? `Request failed with status ${response.status}.`);
  }
  return payload;
}

export async function fetchDossier(id: string): Promise<DragonDossier> {
  const response = await fetch(apiPath(`v1/dossiers/${encodeURIComponent(id)}`), {
    headers: { Accept: "application/json" }
  });
  const payload = await parseResponse<{ dossier: DragonDossier }>(response);
  return payload.dossier;
}

export async function createDossier(dossier: DragonDossier): Promise<SavedDossierResponse> {
  const response = await fetch(apiPath("v1/dossiers"), {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ dossier })
  });
  return parseResponse<SavedDossierResponse>(response);
}

export async function updateDossier(dossier: DragonDossier): Promise<SavedDossierResponse> {
  if (!dossier.id) throw new Error("Save the dossier before updating it.");
  const token = editTokenFromLocation();
  if (!token) throw new Error("This link does not contain editing rights.");
  const response = await fetch(apiPath(`v1/dossiers/${encodeURIComponent(dossier.id)}`), {
    method: "PATCH",
    headers: {
      ...JSON_HEADERS,
      Authorization: `Bearer ${token}`,
      "If-Match": String(dossier.revision)
    },
    body: JSON.stringify({ dossier, revision: dossier.revision })
  });
  return parseResponse<SavedDossierResponse>(response);
}

export async function deleteDossier(id: string): Promise<void> {
  const token = editTokenFromLocation();
  if (!token) throw new Error("This link does not contain editing rights.");
  const response = await fetch(apiPath(`v1/dossiers/${encodeURIComponent(id)}`), {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  });
  await parseResponse<{ deleted: boolean }>(response);
}

export async function forkDossier(id: string): Promise<SavedDossierResponse> {
  const response = await fetch(apiPath(`v1/dossiers/${encodeURIComponent(id)}/fork`), {
    method: "POST",
    headers: JSON_HEADERS,
    body: "{}"
  });
  return parseResponse<SavedDossierResponse>(response);
}

export function dossierIdFromLocation(): string | null {
  const pathMatch = stripAppBase(window.location.pathname).match(/^\/d\/([a-zA-Z0-9_-]+)\/?$/);
  if (pathMatch) return pathMatch[1];
  return new URLSearchParams(window.location.search).get("d");
}

export function hasEditRights(): boolean {
  return Boolean(editTokenFromLocation());
}

export function rememberEditToken(id: string, token: string): void {
  localStorage.setItem(`tiamat-edit-${id}`, token);
}

export interface AiCapabilities {
  enabled: boolean;
  provider: string | null;
  styles: string[];
  qualities: string[];
  priceCents: number;
  currency: string;
  reason: string | null;
}

export interface AiOrderStatus {
  id: string;
  status: string;
  assetUrl: string | null;
  error: string | null;
}

export async function fetchAiCapabilities(): Promise<AiCapabilities> {
  const response = await fetch(apiPath("v1/ai/capabilities"), {
    headers: { Accept: "application/json" }
  });
  return parseResponse<AiCapabilities>(response);
}

export async function createAiOrder(
  dossierId: string,
  style: string,
  quality: string
): Promise<{ orderId: string; claimToken: string; checkoutUrl: string }> {
  const token = editTokenFromLocation();
  if (!token) throw new Error("Editing rights are required to order a portrait.");
  const response = await fetch(apiPath("v1/ai/orders"), {
    method: "POST",
    headers: {
      ...JSON_HEADERS,
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ dossierId, style, quality })
  });
  const order = await parseResponse<{ orderId: string; claimToken: string; checkoutUrl: string }>(
    response
  );
  localStorage.setItem(`tiamat-ai-claim-${order.orderId}`, order.claimToken);
  return order;
}

export async function fetchAiOrder(orderId: string): Promise<AiOrderStatus> {
  const claimToken = localStorage.getItem(`tiamat-ai-claim-${orderId}`);
  if (!claimToken) throw new Error("The order claim token is missing from this browser.");
  const response = await fetch(apiPath(`v1/ai/orders/${encodeURIComponent(orderId)}`), {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${claimToken}`
    }
  });
  return parseResponse<AiOrderStatus>(response);
}
